import { getPlaceCondensersStepId } from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useDeleteStructureStepCompletedMutation,
  useGetStructureStepsCompletedQuery,
  useSaveStructureStepCompletedMutation
} from 'features/api/project-api';
import {
  addManuallyCompletedStep,
  addVisitedStructureStep,
  removeManuallyCompletedStep,
  resetManuallyCompletedSteps,
  setActiveConfigurationStepId,
  setActiveStructureStepId,
  setCompletedStepsFromDatabase,
  setCompletedStepsLoaded,
  setPendingImportCompletedSteps
} from 'features/ui';
import { useEffect, useRef } from 'react';
import {
  buildDeletePayloads,
  buildResetPayloads,
  CONDITIONED_LEVEL_AFFECTED_STEP_IDS,
  getNoSpacesAffectedSteps,
  isBaseLayoutStep
} from 'utils/completed-steps-helpers';
import { isLevelConditioned } from 'utils/structure';

export default function CompletedStepsWatcher() {
  const dispatch = useAppDispatch();
  const { structureUniqueId, structure } = useAppSelector(
    (state) => state.structure
  );
  const manuallyCompletedSteps = useAppSelector(
    (state) => state.ui.manuallyCompletedSteps
  );
  const completedStepsLoaded = useAppSelector(
    (state) => state.ui.completedStepsLoaded
  );
  const pendingImportCompletedSteps = useAppSelector(
    (state) => state.ui.pendingImportCompletedSteps
  );
  const modifiedDefaultCategories = useAppSelector(
    (state) => state.data.modifiedDefaultCategories
  );
  const { configurations, activeConfig: activeConfigIndex } = useAppSelector(
    (state) => state.configurations
  );
  const hasLoadedRef = useRef(false);
  const hadStructureRef = useRef(false);
  const hadSpacesRef = useRef(false);
  const conditionedLevelCountRef = useRef(-1);
  const prevActiveConfigRef = useRef(activeConfigIndex);
  const manuallyCompletedStepsRef = useRef(manuallyCompletedSteps);
  manuallyCompletedStepsRef.current = manuallyCompletedSteps;
  const structureUniqueIdRef = useRef(structureUniqueId);
  structureUniqueIdRef.current = structureUniqueId;
  const structureRef = useRef(structure);
  structureRef.current = structure;

  const [saveStepCompleted] = useSaveStructureStepCompletedMutation();
  const [deleteStepCompleted] = useDeleteStructureStepCompletedMutation();

  const { data: completedStepsData } = useGetStructureStepsCompletedQuery(
    structureUniqueId ?? '',
    { skip: !structureUniqueId }
  );

  useEffect(() => {
    if (completedStepsData && !hasLoadedRef.current) {
      const activeConfigUniqueId = configurations[activeConfigIndex]?.uniqueId;
      const completedStepIds = completedStepsData
        .filter((step) => step.completed)
        .filter(
          (step) =>
            step.configurationUniqueId === null ||
            !activeConfigUniqueId ||
            step.configurationUniqueId === activeConfigUniqueId
        )
        .map((step) => step.subStepCode ?? step.stepCode);
      const currentStructure = structureRef.current;
      dispatch(setCompletedStepsFromDatabase(completedStepIds));
      dispatch(setCompletedStepsLoaded(true));
      hasLoadedRef.current = true;
      conditionedLevelCountRef.current = currentStructure.filter((level) =>
        isLevelConditioned(level)
      ).length;
      hadStructureRef.current = currentStructure.length > 0;
      hadSpacesRef.current = currentStructure.some((level) =>
        level.sections.some((section) => section.spaces.length > 0)
      );
    }
  }, [completedStepsData, dispatch, configurations, activeConfigIndex]);

  useEffect(() => {
    if (!hasLoadedRef.current || !completedStepsData) return;
    if (prevActiveConfigRef.current === activeConfigIndex) return;
    prevActiveConfigRef.current = activeConfigIndex;

    const activeConfigUniqueId = configurations[activeConfigIndex]?.uniqueId;
    const completedStepIds = completedStepsData
      .filter((step) => step.completed)
      .filter(
        (step) =>
          step.configurationUniqueId === null ||
          !activeConfigUniqueId ||
          step.configurationUniqueId === activeConfigUniqueId
      )
      .map((step) => step.subStepCode ?? step.stepCode);
    dispatch(setCompletedStepsFromDatabase(completedStepIds));
    dispatch(setCompletedStepsLoaded(true));
  }, [activeConfigIndex, completedStepsData, configurations, dispatch]);

  useEffect(() => {
    if (!structureUniqueId) {
      hasLoadedRef.current = false;
      hadStructureRef.current = false;
      hadSpacesRef.current = false;
      conditionedLevelCountRef.current = -1;
      dispatch(setCompletedStepsLoaded(false));
    }
  }, [structureUniqueId, dispatch]);

  const structureEmpty = structure.length === 0;

  useEffect(() => {
    if (!hasLoadedRef.current || !structureUniqueId) {
      return;
    }
    if (!structureEmpty) {
      hadStructureRef.current = true;
      return;
    }
    if (!hadStructureRef.current || manuallyCompletedSteps.length === 0) {
      return;
    }
    const baseLayoutSteps = manuallyCompletedSteps.filter(isBaseLayoutStep);
    const otherSteps = manuallyCompletedSteps.filter(
      (id) => !isBaseLayoutStep(id)
    );
    dispatch(resetManuallyCompletedSteps());
    dispatch(setActiveStructureStepId('confirm_details'));
    hadStructureRef.current = false;
    hadSpacesRef.current = false;
    if (baseLayoutSteps.length > 0) {
      const deletePayloads = buildDeletePayloads(
        structureUniqueId,
        baseLayoutSteps
      );
      deleteStepCompleted(deletePayloads).catch(() => {});
    }
    if (otherSteps.length > 0) {
      const resetPayloads = buildResetPayloads(structureUniqueId, otherSteps);
      saveStepCompleted(resetPayloads).catch(() => {});
    }
  }, [
    structureEmpty,
    structureUniqueId,
    manuallyCompletedSteps,
    dispatch,
    saveStepCompleted,
    deleteStepCompleted
  ]);

  const hasSpaces = structure.some((level) =>
    level.sections.some((section) => section.spaces.length > 0)
  );

  useEffect(() => {
    if (!hasLoadedRef.current || !structureUniqueId) {
      return;
    }
    if (hasSpaces) {
      hadSpacesRef.current = true;
      return;
    }
    if (!hadSpacesRef.current) {
      return;
    }
    const noSpacesAffected = getNoSpacesAffectedSteps(manuallyCompletedSteps);
    if (noSpacesAffected.length === 0) {
      return;
    }
    noSpacesAffected.forEach((stepId) => {
      dispatch(removeManuallyCompletedStep(stepId));
    });
    dispatch(setActiveStructureStepId('add_spaces'));
    hadSpacesRef.current = false;
    const baseLayoutSteps = noSpacesAffected.filter(isBaseLayoutStep);
    const otherSteps = noSpacesAffected.filter((id) => !isBaseLayoutStep(id));
    if (baseLayoutSteps.length > 0) {
      const deletePayloads = buildDeletePayloads(
        structureUniqueId,
        baseLayoutSteps
      );
      deleteStepCompleted(deletePayloads).catch(() => {});
    }
    if (otherSteps.length > 0) {
      const resetPayloads = buildResetPayloads(structureUniqueId, otherSteps);
      saveStepCompleted(resetPayloads).catch(() => {});
    }
  }, [
    hasSpaces,
    manuallyCompletedSteps,
    structure,
    structureUniqueId,
    dispatch,
    saveStepCompleted,
    deleteStepCompleted
  ]);

  const conditionedLevelCount = structure.filter((l) =>
    isLevelConditioned(l)
  ).length;

  useEffect(() => {
    if (
      !hasLoadedRef.current ||
      !structureUniqueId ||
      pendingImportCompletedSteps
    ) {
      conditionedLevelCountRef.current = conditionedLevelCount;
      return;
    }

    const previousCount = conditionedLevelCountRef.current;
    conditionedLevelCountRef.current = conditionedLevelCount;

    if (previousCount === -1) return;

    if (conditionedLevelCount === 0 && previousCount > 0) {
      dispatch(setActiveStructureStepId('configure_level_details'));
      if (manuallyCompletedSteps.length === 0) return;

      const baseLayoutSteps = manuallyCompletedSteps.filter(isBaseLayoutStep);
      const otherSteps = manuallyCompletedSteps.filter(
        (id) => !isBaseLayoutStep(id)
      );
      dispatch(resetManuallyCompletedSteps());
      hadStructureRef.current = false;
      hadSpacesRef.current = false;
      if (baseLayoutSteps.length > 0) {
        const deletePayloads = buildDeletePayloads(
          structureUniqueId,
          baseLayoutSteps
        );
        deleteStepCompleted(deletePayloads).catch(() => {});
      }
      if (otherSteps.length > 0) {
        const resetPayloads = buildResetPayloads(structureUniqueId, otherSteps);
        saveStepCompleted(resetPayloads).catch(() => {});
      }
      return;
    }

    if (conditionedLevelCount <= previousCount) return;

    const stepsToReset = manuallyCompletedSteps.filter((id) =>
      CONDITIONED_LEVEL_AFFECTED_STEP_IDS.includes(id)
    );

    if (stepsToReset.length === 0) return;

    stepsToReset.forEach((stepId) => {
      dispatch(removeManuallyCompletedStep(stepId));
    });

    const resetPayloads = buildResetPayloads(structureUniqueId, stepsToReset);
    saveStepCompleted(resetPayloads).catch(() => {});
  }, [
    conditionedLevelCount,
    manuallyCompletedSteps,
    structureUniqueId,
    pendingImportCompletedSteps,
    dispatch,
    saveStepCompleted,
    deleteStepCompleted
  ]);

  const currentConfig = configurations[activeConfigIndex];
  const hasNonHiddenCondenser =
    currentConfig?.condensers.some((c) => c.position !== undefined) ?? false;

  useEffect(() => {
    if (!currentConfig || !structureUniqueId) return;

    const condenserStepId = getPlaceCondensersStepId(currentConfig.uniqueId);
    const isCondenserStepCompleted =
      manuallyCompletedSteps.includes(condenserStepId);

    if (hasNonHiddenCondenser || !isCondenserStepCompleted) return;

    dispatch(removeManuallyCompletedStep(condenserStepId));
    dispatch(removeManuallyCompletedStep('condensers'));
    dispatch(setActiveConfigurationStepId('condensers'));
    saveStepCompleted({
      structureUniqueId,
      stepCode: 'condensers',
      subStepCode: condenserStepId,
      completed: false,
      configurationUniqueId: currentConfig.uniqueId
    }).catch(() => {});
  }, [
    currentConfig,
    hasNonHiddenCondenser,
    manuallyCompletedSteps,
    structureUniqueId,
    dispatch,
    saveStepCompleted
  ]);

  useEffect(() => {
    const hasModifications = Object.values(modifiedDefaultCategories).some(
      Boolean
    );
    if (!hasModifications) return;
    if (manuallyCompletedStepsRef.current.includes('sizing_tuning')) return;

    dispatch(addManuallyCompletedStep('sizing_tuning'));

    const currentStructureUniqueId = structureUniqueIdRef.current;
    if (!currentStructureUniqueId) return;

    saveStepCompleted({
      structureUniqueId: currentStructureUniqueId,
      stepCode: 'sizing_tuning',
      subStepCode: 'sizing_tuning',
      completed: true
    })
      .unwrap()
      .catch((err) => console.error('Failed to save step completed:', err));
  }, [modifiedDefaultCategories, dispatch, saveStepCompleted]);

  useEffect(() => {
    if (!pendingImportCompletedSteps) {
      return;
    }
    if (
      !structureUniqueId ||
      (!completedStepsLoaded && !hasLoadedRef.current)
    ) {
      return;
    }

    dispatch(setPendingImportCompletedSteps(null));
    pendingImportCompletedSteps.forEach((step) => {
      dispatch(addManuallyCompletedStep(step.subStepCode ?? step.stepCode));
      if (step.subStepCode) {
        dispatch(addVisitedStructureStep(step.subStepCode));
      }
    });
    saveStepCompleted(
      pendingImportCompletedSteps.map((step) => ({
        structureUniqueId,
        stepCode: step.stepCode,
        subStepCode: step.subStepCode,
        completed: true
      }))
    )
      .unwrap()
      .catch((err) => console.error('Failed to save steps completed:', err));
  }, [
    pendingImportCompletedSteps,
    structureUniqueId,
    completedStepsLoaded,
    dispatch,
    saveStepCompleted
  ]);

  return null;
}
